import { TextLayout } from '../text-layout'
import { BoxConstraints, Font, PaintOp, RenderBox, TextAlign } from '../rendering'
import { renderElement } from '../document'

type TextProps = {
  text: string
  font: Font
  color: string
  lineHeight: number
  textAlign: TextAlign
  lineThrough?: boolean
  // Controls inserted line breaks only; explicit newlines always remain.
  softWrap: boolean
}

/**
 * Uses Flutter's parent-width layout model: with softWrap
 * a bounded width wraps the text, and the box is
 * constraints.constrain(min(unwrapped width, maxWidth) × wrapped height). Paint
 * carries the measured lines, so every surface draws the lines laid out here.
 * Without softWrap, source lines stay whole and paint past a narrower box.
 * This renderer currently paints overflow visibly; unlike Flutter's default
 * Text overflow policy, it does not clip glyphs to these bounds.
 */
export class TextRenderBox extends RenderBox<TextProps> {
  lines: string[] = []

  performLayout(c: BoxConstraints) {
    const { text, font, lineHeight, softWrap } = this.props
    const natural = TextLayout.layout(text, font, lineHeight)
    let layout = natural
    // Use the same metric calculation for intrinsic sizing and line breaking.
    // Text that fits its measured width must retain its original source lines.
    if (softWrap && natural.width > c.maxWidth) {
      layout = TextLayout.layout(text, font, lineHeight, c.maxWidth)
    }
    this.lines = layout.lines.map((line) => line.text)

    this.box = {
      x: 0,
      y: 0,
      ...BoxConstraints.constrain(c, {
        width: Math.min(natural.width, c.maxWidth),
        height: layout.height
      })
    }
  }

  draw(): PaintOp {
    return {
      type: 'text',
      bounds: this.box,
      text: this.lines.join('\n'),
      font: this.props.font,
      color: this.props.color,
      line_height: this.props.lineHeight,
      line_through: this.props.lineThrough,
      kerning: false,
      text_align: this.props.textAlign // convey alignment intent to renderer
    }
  }
}

export function Text({
  children,
  fontSize = 14,
  fontFamily = 'Helvetica',
  fontWeight = 'normal',
  fontStyle = 'normal',
  color = '#000000',
  lineHeight = 1.0,
  textAlign = 'left',
  lineThrough,
  softWrap = true
}: {
  children: string
  fontSize: number
  fontFamily?: string
  fontWeight?: Font['weight']
  fontStyle?: Font['style']
  color?: string
  lineHeight?: number
  textAlign?: TextAlign
  lineThrough?: boolean
  softWrap?: boolean
}) {
  const font: Font = { size: fontSize, family: fontFamily, weight: fontWeight }
  if (fontStyle == 'italic') font.style = fontStyle

  return renderElement(
    new TextRenderBox({
      text: children,
      font,
      color,
      lineHeight,
      textAlign,
      lineThrough,
      softWrap
    })
  )
}
